// Internationalization (i18n) Configuration
// Supports: English, Spanish, French, German, Dutch, Chinese, Japanese, Russian, Portuguese, Arabic

export type LanguageCode = 'en' | 'es' | 'fr' | 'de' | 'nl' | 'zh' | 'ja' | 'ru' | 'pt' | 'ar';

export interface Language {
  code: LanguageCode;
  name: string;
  nativeName: string;
  flag: string;
  rtl?: boolean; // Right-to-left languages
}

export const LANGUAGES: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', rtl: true },
];

// UI Translations
export const UI_TRANSLATIONS: Record<LanguageCode, Record<string, string>> = {
  en: {
    title: 'NEBULA MAZE',
    subtitle: 'Daily MazeTest',
    tagline: 'Fog of War • Survival • Trivia',
    easy: 'Easy',
    hard: 'Hard',
    toughGenius: 'Tough Genius',
    startAdventure: 'START ADVENTURE',
    pressToBegin: 'Press here to begin!',
    resetProgress: 'RESET PROGRESS',
    shareTheFun: 'Share the Fun!',
    challengeFriend: 'Challenge a Friend!',
    testYourFriends: 'Test Your Friends!',
    sponsored: 'SPONSORED',
    visitPartners: 'Visit our partners for exclusive deals',
    howToPlay: 'HOW TO PLAY',
    howToPlayDesc: 'Use arrow keys or swipe to navigate the maze',
    explorerIcon: 'EXPLORER ICON',
    score: 'SCORE',
    health: 'HEALTH',
    inventory: 'INVENTORY',
    nerdMeter: 'NERD METER',
    levelComplete: 'Level Complete!',
    gameOver: 'Game Over',
    tryAgain: 'Try Again',
    nextLevel: 'Next Level',
    academicChallenge: 'Academic Challenge',
    submitAnswer: 'Submit Answer',
    skipQuestion: 'Skip Question',
    correct: 'Correct!',
    incorrect: 'Incorrect!',
    hazardEncounter: 'Hazard Encounter!',
    chooseItem: 'Choose an item to counter:',
    level: 'LEVEL',
    takeDamage: 'Take Damage (No Item)',
    exploreMore: 'Explore More',
    exploreMoreMsg: 'The exit is locked! Explore 80% of the map to unlock.',
    solveForPoints: 'Solve this to gain points!',
    advertisement: 'Advertisement Here',
    advertisementSpace: 'ADVERTISEMENT SPACE',
    exclusiveDeals: 'EXCLUSIVE DEALS',
    visitOurPartners: 'VISIT OUR PARTNERS',
  },
  es: {
    title: 'NEBULA MAZE',
    subtitle: 'Prueba Diaria del Laberinto',
    tagline: 'Niebla de Guerra • Supervivencia • Trivia',
    easy: 'Fácil',
    hard: 'Difícil',
    toughGenius: 'Genio Difícil',
    startAdventure: 'COMENZAR AVENTURA',
    pressToBegin: '¡Presiona aquí para comenzar!',
    resetProgress: 'REINICIAR PROGRESO',
    shareTheFun: '¡Comparte la Diversión!',
    challengeFriend: '¡Desafía a un Amigo!',
    testYourFriends: '¡Pon a Prueba a tus Amigos!',
    sponsored: 'PATROCINADO',
    visitPartners: 'Visita nuestros socios para ofertas exclusivas',
    howToPlay: 'CÓMO JUGAR',
    howToPlayDesc: 'Usa las flechas o desliza para navegar el laberinto',
    explorerIcon: 'ICONO DE EXPLORADOR',
    score: 'PUNTOS',
    health: 'SALUD',
    inventory: 'INVENTARIO',
    nerdMeter: 'MEDIDOR NERD',
    levelComplete: '¡Nivel Completado!',
    gameOver: 'Fin del Juego',
    tryAgain: 'Intentar de Nuevo',
    nextLevel: 'Siguiente Nivel',
    academicChallenge: 'Desafío Académico',
    submitAnswer: 'Enviar Respuesta',
    skipQuestion: 'Saltar Pregunta',
    correct: '¡Correcto!',
    incorrect: '¡Incorrecto!',
    hazardEncounter: '¡Encuentro Peligroso!',
    chooseItem: 'Elige un objeto para contrarrestar:',
    level: 'NIVEL',
    takeDamage: 'Recibir Daño (Sin Objeto)',
    exploreMore: 'Explorar Más',
    exploreMoreMsg: '¡La salida está bloqueada! Explora el 80% del mapa para desbloquear.',
    solveForPoints: '¡Resuelve esto para ganar puntos!',
    advertisement: 'Publicidad Aquí',
    advertisementSpace: 'ESPACIO PUBLICITARIO',
    exclusiveDeals: 'OFERTAS EXCLUSIVAS',
    visitOurPartners: 'VISITA NUESTROS SOCIOS',
  },
  fr: {
    title: 'NEBULA MAZE',
    subtitle: 'Test du Labyrinthe Quotidien',
    tagline: 'Brouillard de Guerre • Survie • Trivia',
    easy: 'Facile',
    hard: 'Difficile',
    toughGenius: 'Génie Difficile',
    startAdventure: 'COMMENCER L\'AVENTURE',
    pressToBegin: 'Appuyez ici pour commencer!',
    resetProgress: 'RÉINITIALISER',
    shareTheFun: 'Partagez le Plaisir!',
    challengeFriend: 'Défiez un Ami!',
    testYourFriends: 'Testez vos Amis!',
    sponsored: 'SPONSORISÉ',
    visitPartners: 'Visitez nos partenaires pour des offres exclusives',
    howToPlay: 'COMMENT JOUER',
    howToPlayDesc: 'Utilisez les flèches ou glissez pour naviguer',
    explorerIcon: 'ICÔNE D\'EXPLORATEUR',
    score: 'SCORE',
    health: 'SANTÉ',
    inventory: 'INVENTAIRE',
    nerdMeter: 'COMPTEUR NERD',
    levelComplete: 'Niveau Terminé!',
    gameOver: 'Fin de Partie',
    tryAgain: 'Réessayer',
    nextLevel: 'Niveau Suivant',
    academicChallenge: 'Défi Académique',
    submitAnswer: 'Soumettre',
    skipQuestion: 'Passer',
    correct: 'Correct!',
    incorrect: 'Incorrect!',
    hazardEncounter: 'Rencontre Dangereuse!',
    chooseItem: 'Choisissez un objet:',
    level: 'NIVEAU',
    takeDamage: 'Subir des Dégâts (Sans Objet)',
    exploreMore: 'Explorer Plus',
    exploreMoreMsg: 'La sortie est verrouillée ! Explorez 80% de la carte.',
    solveForPoints: 'Résolvez ceci pour gagner des points !',
    advertisement: 'Publicité Ici',
    advertisementSpace: 'ESPACE PUBLICITAIRE',
    exclusiveDeals: 'OFFRES EXCLUSIVES',
    visitOurPartners: 'VISITEZ NOS PARTENAIRES',
  },
  de: {
    title: 'NEBULA MAZE',
    subtitle: 'Täglicher Labyrinth-Test',
    tagline: 'Kriegsnebel • Überleben • Trivia',
    easy: 'Leicht',
    hard: 'Schwer',
    toughGenius: 'Hartes Genie',
    startAdventure: 'ABENTEUER STARTEN',
    pressToBegin: 'Hier drücken zum Starten!',
    resetProgress: 'FORTSCHRITT ZURÜCKSETZEN',
    shareTheFun: 'Teile den Spaß!',
    challengeFriend: 'Fordere einen Freund heraus!',
    testYourFriends: 'Teste deine Freunde!',
    sponsored: 'GESPONSERT',
    visitPartners: 'Besuchen Sie unsere Partner für exklusive Angebote',
    howToPlay: 'WIE MAN SPIELT',
    howToPlayDesc: 'Pfeiltasten oder wischen zum Navigieren',
    explorerIcon: 'ENTDECKER-SYMBOL',
    score: 'PUNKTE',
    health: 'GESUNDHEIT',
    inventory: 'INVENTAR',
    nerdMeter: 'NERD-METER',
    levelComplete: 'Level Abgeschlossen!',
    gameOver: 'Spiel Vorbei',
    tryAgain: 'Nochmal Versuchen',
    nextLevel: 'Nächstes Level',
    academicChallenge: 'Akademische Herausforderung',
    submitAnswer: 'Antwort Senden',
    skipQuestion: 'Frage Überspringen',
    correct: 'Richtig!',
    incorrect: 'Falsch!',
    hazardEncounter: 'Gefahr Begegnung!',
    chooseItem: 'Wähle einen Gegenstand:',
    level: 'LEVEL',
    takeDamage: 'Schaden Nehmen (Kein Item)',
    exploreMore: 'Mehr Erkunden',
    exploreMoreMsg: 'Ausgang gesperrt! Erkunde 80% der Karte zum Entsperren.',
    solveForPoints: 'Löse dies für Punkte!',
    advertisement: 'Werbung Hier',
    advertisementSpace: 'WERBEFLÄCHE',
    exclusiveDeals: 'EXKLUSIVE ANGEBOTE',
    visitOurPartners: 'BESUCHEN SIE UNSERE PARTNER',
  },
  nl: {
    title: 'NEBULA MAZE',
    subtitle: 'Dagelijkse Doolhof Test',
    tagline: 'Oorlogsmist • Overleven • Trivia',
    easy: 'Makkelijk',
    hard: 'Moeilijk',
    toughGenius: 'Moeilijk Genie',
    startAdventure: 'START AVONTUUR',
    pressToBegin: 'Druk hier om te beginnen!',
    resetProgress: 'VOORTGANG RESETTEN',
    shareTheFun: 'Deel het Plezier!',
    challengeFriend: 'Daag een Vriend Uit!',
    testYourFriends: 'Test je Vrienden!',
    sponsored: 'GESPONSORD',
    visitPartners: 'Bezoek onze partners voor exclusieve deals',
    howToPlay: 'HOE TE SPELEN',
    howToPlayDesc: 'Gebruik pijltjestoetsen of veeg om te navigeren',
    explorerIcon: 'VERKENNER ICOON',
    score: 'SCORE',
    health: 'GEZONDHEID',
    inventory: 'INVENTARIS',
    nerdMeter: 'NERD METER',
    levelComplete: 'Level Voltooid!',
    gameOver: 'Game Over',
    tryAgain: 'Opnieuw Proberen',
    nextLevel: 'Volgend Level',
    academicChallenge: 'Academische Uitdaging',
    submitAnswer: 'Antwoord Indienen',
    skipQuestion: 'Vraag Overslaan',
    correct: 'Correct!',
    incorrect: 'Onjuist!',
    hazardEncounter: 'Gevaar Ontmoeting!',
    chooseItem: 'Kies een item:',
    level: 'LEVEL',
    takeDamage: 'Schade Nemen (Geen Item)',
    exploreMore: 'Meer Verkennen',
    exploreMoreMsg: 'De uitgang is vergrendeld! Verken 80% van de kaart.',
    solveForPoints: 'Los dit op voor punten!',
    advertisement: 'Advertentie Hier',
    advertisementSpace: 'ADVERTENTIERUIMTE',
    exclusiveDeals: 'EXCLUSIEVE AANBIEDINGEN',
    visitOurPartners: 'BEZOEK ONZE PARTNERS',
  },
  zh: {
    title: '星云迷宫',
    subtitle: '每日迷宫测试',
    tagline: '战争迷雾 • 生存 • 问答',
    easy: '简单',
    hard: '困难',
    toughGenius: '天才难度',
    startAdventure: '开始冒险',
    pressToBegin: '点击这里开始！',
    resetProgress: '重置进度',
    shareTheFun: '分享乐趣！',
    challengeFriend: '挑战朋友！',
    testYourFriends: '测试你的朋友！',
    sponsored: '赞助商',
    visitPartners: '访问我们的合作伙伴获取独家优惠',
    howToPlay: '游戏说明',
    howToPlayDesc: '使用方向键或滑动来导航迷宫',
    explorerIcon: '探险家图标',
    score: '分数',
    health: '生命值',
    inventory: '物品栏',
    nerdMeter: '学霸指数',
    levelComplete: '关卡完成！',
    gameOver: '游戏结束',
    tryAgain: '再试一次',
    nextLevel: '下一关',
    academicChallenge: '学术挑战',
    submitAnswer: '提交答案',
    skipQuestion: '跳过问题',
    correct: '正确！',
    incorrect: '错误！',
    hazardEncounter: '遇到危险！',
    chooseItem: '选择一个物品来应对：',
    level: '关卡',
    takeDamage: '受伤（无道具）',
    exploreMore: '继续探索',
    exploreMoreMsg: '出口已锁定！探索80%的地图以解锁。',
    solveForPoints: '解答此题获得积分！',
    advertisement: '广告位',
    advertisementSpace: '广告空间',
    exclusiveDeals: '独家优惠',
    visitOurPartners: '访问我们的合作伙伴',
  },
  ja: {
    title: 'ネビュラメイズ',
    subtitle: '毎日の迷路テスト',
    tagline: '戦場の霧 • サバイバル • トリビア',
    easy: '簡単',
    hard: '難しい',
    toughGenius: '天才級',
    startAdventure: '冒険を始める',
    pressToBegin: 'ここを押して始めましょう！',
    resetProgress: '進行状況をリセット',
    shareTheFun: '楽しさを共有！',
    challengeFriend: '友達に挑戦！',
    testYourFriends: '友達をテスト！',
    sponsored: 'スポンサー',
    visitPartners: 'パートナーを訪問して特別オファーを',
    howToPlay: '遊び方',
    howToPlayDesc: '矢印キーまたはスワイプで迷路を移動',
    explorerIcon: '探検家アイコン',
    score: 'スコア',
    health: '体力',
    inventory: 'インベントリ',
    nerdMeter: 'オタクメーター',
    levelComplete: 'レベルクリア！',
    gameOver: 'ゲームオーバー',
    tryAgain: 'もう一度',
    nextLevel: '次のレベル',
    academicChallenge: '学術チャレンジ',
    submitAnswer: '回答を送信',
    skipQuestion: 'スキップ',
    correct: '正解！',
    incorrect: '不正解！',
    hazardEncounter: '危険遭遇！',
    chooseItem: 'アイテムを選択：',
    level: 'レベル',
    takeDamage: 'ダメージを受ける（アイテムなし）',
    exploreMore: 'もっと探索',
    exploreMoreMsg: '出口はロック中！マップの80%を探索してください。',
    solveForPoints: 'これを解いてポイントを獲得しよう！',
    advertisement: '広告',
    advertisementSpace: '広告スペース',
    exclusiveDeals: '限定オファー',
    visitOurPartners: 'パートナーを訪問',
  },
  ru: {
    title: 'НЕБУЛА ЛАБИРИНТ',
    subtitle: 'Ежедневный Тест Лабиринта',
    tagline: 'Туман Войны • Выживание • Викторина',
    easy: 'Легко',
    hard: 'Сложно',
    toughGenius: 'Гений',
    startAdventure: 'НАЧАТЬ ПРИКЛЮЧЕНИЕ',
    pressToBegin: 'Нажмите здесь, чтобы начать!',
    resetProgress: 'СБРОСИТЬ ПРОГРЕСС',
    shareTheFun: 'Поделись Весельем!',
    challengeFriend: 'Бросьте Вызов Другу!',
    testYourFriends: 'Проверьте Друзей!',
    sponsored: 'СПОНСОР',
    visitPartners: 'Посетите наших партнеров для эксклюзивных предложений',
    howToPlay: 'КАК ИГРАТЬ',
    howToPlayDesc: 'Используйте стрелки или свайп для навигации',
    explorerIcon: 'ИКОНКА ИССЛЕДОВАТЕЛЯ',
    score: 'СЧЁТ',
    health: 'ЗДОРОВЬЕ',
    inventory: 'ИНВЕНТАРЬ',
    nerdMeter: 'УРОВЕНЬ БОТАНИКА',
    levelComplete: 'Уровень Пройден!',
    gameOver: 'Игра Окончена',
    tryAgain: 'Попробовать Снова',
    nextLevel: 'Следующий Уровень',
    academicChallenge: 'Академический Вызов',
    submitAnswer: 'Отправить Ответ',
    skipQuestion: 'Пропустить Вопрос',
    correct: 'Правильно!',
    incorrect: 'Неправильно!',
    hazardEncounter: 'Опасная Встреча!',
    chooseItem: 'Выберите предмет:',
    level: 'УРОВЕНЬ',
    takeDamage: 'Получить Урон (Нет Предмета)',
    exploreMore: 'Исследуй Больше',
    exploreMoreMsg: 'Выход заблокирован! Исследуй 80% карты для разблокировки.',
    solveForPoints: 'Реши это, чтобы получить очки!',
    advertisement: 'Реклама Здесь',
    advertisementSpace: 'РЕКЛАМНОЕ МЕСТО',
    exclusiveDeals: 'ЭКСКЛЮЗИВНЫЕ ПРЕДЛОЖЕНИЯ',
    visitOurPartners: 'ПОСЕТИТЕ НАШИХ ПАРТНЕРОВ',
  },
  pt: {
    title: 'NEBULA MAZE',
    subtitle: 'Teste Diário do Labirinto',
    tagline: 'Névoa de Guerra • Sobrevivência • Trivia',
    easy: 'Fácil',
    hard: 'Difícil',
    toughGenius: 'Gênio Difícil',
    startAdventure: 'INICIAR AVENTURA',
    pressToBegin: 'Pressione aqui para começar!',
    resetProgress: 'RESETAR PROGRESSO',
    shareTheFun: 'Compartilhe a Diversão!',
    challengeFriend: 'Desafie um Amigo!',
    testYourFriends: 'Teste seus Amigos!',
    sponsored: 'PATROCINADO',
    visitPartners: 'Visite nossos parceiros para ofertas exclusivas',
    howToPlay: 'COMO JOGAR',
    howToPlayDesc: 'Use as setas ou deslize para navegar no labirinto',
    explorerIcon: 'ÍCONE DO EXPLORADOR',
    score: 'PONTUAÇÃO',
    health: 'SAÚDE',
    inventory: 'INVENTÁRIO',
    nerdMeter: 'MEDIDOR NERD',
    levelComplete: 'Nível Completo!',
    gameOver: 'Fim de Jogo',
    tryAgain: 'Tentar Novamente',
    nextLevel: 'Próximo Nível',
    academicChallenge: 'Desafio Acadêmico',
    submitAnswer: 'Enviar Resposta',
    skipQuestion: 'Pular Pergunta',
    correct: 'Correto!',
    incorrect: 'Incorreto!',
    hazardEncounter: 'Encontro Perigoso!',
    chooseItem: 'Escolha um item:',
    level: 'NÍVEL',
    takeDamage: 'Sofrer Dano (Sem Item)',
    exploreMore: 'Explorar Mais',
    exploreMoreMsg: 'A saída está bloqueada! Explore 80% do mapa para desbloquear.',
    solveForPoints: 'Resolva isso para ganhar pontos!',
    advertisement: 'Publicidade Aqui',
    advertisementSpace: 'ESPAÇO PUBLICITÁRIO',
    exclusiveDeals: 'OFERTAS EXCLUSIVAS',
    visitOurPartners: 'VISITE NOSSOS PARCEIROS',
  },
  ar: {
    title: 'متاهة السديم',
    subtitle: 'اختبار المتاهة اليومي',
    tagline: 'ضباب الحرب • البقاء • الأسئلة',
    easy: 'سهل',
    hard: 'صعب',
    toughGenius: 'عبقري',
    startAdventure: 'ابدأ المغامرة',
    pressToBegin: 'اضغط هنا للبدء!',
    resetProgress: 'إعادة تعيين التقدم',
    shareTheFun: 'شارك المرح!',
    challengeFriend: 'تحدَّ صديقاً!',
    testYourFriends: 'اختبر أصدقاءك!',
    sponsored: 'برعاية',
    visitPartners: 'زُر شركاءنا للعروض الحصرية',
    howToPlay: 'كيف تلعب',
    howToPlayDesc: 'استخدم مفاتيح الأسهم أو اسحب للتنقل',
    explorerIcon: 'أيقونة المستكشف',
    score: 'النقاط',
    health: 'الصحة',
    inventory: 'المخزون',
    nerdMeter: 'مقياس الذكاء',
    levelComplete: 'اكتمل المستوى!',
    gameOver: 'انتهت اللعبة',
    tryAgain: 'حاول مرة أخرى',
    nextLevel: 'المستوى التالي',
    academicChallenge: 'تحدي أكاديمي',
    submitAnswer: 'إرسال الإجابة',
    skipQuestion: 'تخطي السؤال',
    correct: 'صحيح!',
    incorrect: 'خطأ!',
    hazardEncounter: 'مواجهة خطر!',
    chooseItem: 'اختر عنصراً:',
    level: 'المستوى',
    takeDamage: 'تلقي الضرر (بدون عنصر)',
    exploreMore: 'استكشف أكثر',
    exploreMoreMsg: 'المخرج مقفل! استكشف 80% من الخريطة للفتح.',
    solveForPoints: 'أجب على هذا لكسب النقاط!',
    advertisement: 'إعلان هنا',
    advertisementSpace: 'مساحة إعلانية',
    exclusiveDeals: 'عروض حصرية',
    visitOurPartners: 'زُر شركاءنا',
  },
};

// Detect browser/device language
export function detectLanguage(): LanguageCode {
  const browserLang = navigator.language || (navigator as any).userLanguage || 'en';
  const langCode = browserLang.split('-')[0].toLowerCase();

  // Map to supported languages
  const supportedCodes: LanguageCode[] = ['en', 'es', 'fr', 'de', 'nl', 'zh', 'ja', 'ru', 'pt', 'ar'];

  if (supportedCodes.includes(langCode as LanguageCode)) {
    return langCode as LanguageCode;
  }

  // Default to English
  return 'en';
}

// Get translation
export function t(key: string, lang: LanguageCode = 'en'): string {
  return UI_TRANSLATIONS[lang]?.[key] || UI_TRANSLATIONS['en']?.[key] || key;
}

// Get language info
export function getLanguageInfo(code: LanguageCode): Language | undefined {
  return LANGUAGES.find(l => l.code === code);
}

// Save language preference
export function saveLanguagePreference(code: LanguageCode): void {
  localStorage.setItem('nebula-maze-language', code);
}

// Load language preference
export function loadLanguagePreference(): LanguageCode {
  const saved = localStorage.getItem('nebula-maze-language');
  if (saved && LANGUAGES.some(l => l.code === saved)) {
    return saved as LanguageCode;
  }
  return detectLanguage();
}
